// ============================================
// SHARED ESCAPING + FORMAT HELPERS (TASK-862)
// ============================================
// Side-effect module: attaches canonical helpers to `window.*`. Imported FIRST
// by app.js (before any view module) so the helpers exist before the statically-
// imported views evaluate. Views reference these via `window.*` (NOT a cross-`?v=`
// ES import — Pitfall #1) through a one-line hoisted delegating wrapper, e.g.
//   function escapeHtml(str) { return window.escapeHtml(str); }
// The wrapper defers the window lookup to CALL time, which is why an eager
// `const escapeHtml = window.escapeHtml` would be wrong here — view modules
// evaluate before app.js's body, so an eager capture grabs `undefined`.
//
// BEHAVIORAL EQUIVALENCE (TASK-862, security-sensitive): `window.escapeHtml`
// escapes EXACTLY &<>"' ('→&#39;) — character-for-character identical to the
// `!str`-guarded view variants it replaces (billing/clock/compliance/timeoff/
// jsa/maintenance/pretrip/roster). It is NOT used to replace variants that
// escape a DIFFERENT character set or use a different guard:
//   - app.js's DOM-based escapeHtml escapes only &<> (no quotes) — left as-is.
//   - dispatch.js / reconciliation.js / sidebarCustomize.js use a `str == null`
//     guard (so escapeHtml(0) -> "0", not "") — left as-is.
//   - ap.js / crm.js / jobboard.js / dispatch inline escape &<>" (no apostrophe).
//   - dotAdvisor / permitPete escape with '->&#039; (byte-different output).
//   - the escAttr family each escape a DIFFERENT subset (see below) — left as-is.
// Collapsing any of those would change escaped output (XSS context or byte
// output), which is forbidden for this refactor.

(function () {
  'use strict';

  // Canonical HTML TEXT-context escaper. Escapes &<>"' ('->&#39;).
  // Byte-identical to the `if(!str) return ''` + map view variants.
  window.escapeHtml = function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"']/g, function (m) {
      return ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[m];
    });
  };

  // Canonical HTML ATTRIBUTE-context escaper. Escapes the FULL set &<>"'
  // ('->&#39;). Provided for new call sites. NOTE: the existing per-view escAttr
  // helpers each escape a DIFFERENT (smaller) subset and were intentionally left
  // in place — repointing them at this stronger escaper would change byte output.
  window.escAttr = function escAttr(str) {
    if (str == null) return '';
    return String(str).replace(/[&<>"']/g, function (m) {
      return ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[m];
    });
  };

  // ==========================================================================
  // TASK-4838 — canonical escaper for a JS STRING LITERAL that lives inside an
  // HTML EVENT-HANDLER attribute, i.e. the shape this codebase writes hundreds
  // of times:  onclick="window.Mod.fn('${…}')"
  // ==========================================================================
  // That position is TWO nested contexts, and escapeHtml/escAttr only handle the
  // outer one. The browser parses the attribute value FIRST — decoding character
  // references — and only then compiles the result as JavaScript. So escAttr's
  // `'` -> `&#39;` is undone by the parser BEFORE the JS is compiled, and the
  // apostrophe lands back in the source, closing the string literal.
  //
  // Proven in a real Chromium DOM (tests/task4838-handler-jsstring.spec.js), not
  // argued from a lesson file:
  //   escapeHtml("O'Brien")            -> O&#39;Brien
  //   onclick="f('O&#39;Brien')"       -> parser decodes -> f('O'Brien')
  //   -> SyntaxError, el.onclick === null, the BUTTON SILENTLY DOES NOTHING.
  //   escapeHtml("x');alert(1);//")    -> the same decode hands the parser a
  //   closed string plus attacker statements, and they EXECUTE.
  // So on this surface escAttr is not a weaker fix than escJsAttr — it is not a
  // fix at all, and neither is escapeHtml.
  //
  // Correct order is inside-out: escape for the JS string literal, THEN escape
  // that for the attribute, because the parser unwraps the attribute layer first.
  // The callee receives the byte-identical original string (JS un-escapes `\'`),
  // so nothing downstream sees a changed value.
  window.escJsAttr = function escJsAttr(str) {
    if (str == null) return '';
    var js = String(str)
      .replace(/\\/g, '\\\\')     // must come first, or it doubles our own escapes
      .replace(/'/g, "\\'")
      .replace(/\r/g, '\\r')
      .replace(/\n/g, '\\n');
    return window.escAttr(js);
  };

  // ==========================================================================
  // TASK-1421 — canonical PUBLIC ACCESS TOKEN minter.
  // ==========================================================================
  // An accessTokens doc ID *is* the bearer credential: it goes in the URL
  // (?access= / ?docs= / ?sign=) and anyone holding it gets in. Every mint site
  // used to build it from a known id plus a timestamp, e.g.
  //   tenantId + '_docs_' + Date.now().toString(36)
  // The tenant slug is public, so guessing that reduced to "which millisecond"
  // — roughly 86M candidates for a known day, far fewer if you can guess the
  // hour, against a token that never expires. TASK-1420 closed *enumeration*;
  // this closes *guessing*, which is the other half.
  //
  // Safe to change the format: nothing anywhere parses a token id. Every
  // consumer looks the doc up and reads its FIELDS (tenantId / projectId /
  // orderId / opportunityId / type), verified before this landed. Existing
  // tokens keep working untouched — this only changes how NEW ones are minted,
  // so no live customer or driver link breaks.
  //
  // The `prefix` is for humans reading the console/Firestore only. It must
  // never carry an entity id: that is what made these guessable.
  window.mintAccessTokenId = function mintAccessTokenId(prefix) {
    const c = (typeof crypto !== 'undefined') ? crypto : null;
    let rand;
    if (c && typeof c.randomUUID === 'function') {
      rand = c.randomUUID().replace(/-/g, '');
    } else if (c && typeof c.getRandomValues === 'function') {
      const b = new Uint8Array(16);
      c.getRandomValues(b);
      rand = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    } else {
      // DELIBERATELY NO Math.random() FALLBACK. A silently-weak bearer token is
      // worse than a visible failure — it looks like it worked and is guessable
      // forever. Every browser that can run this app has WebCrypto.
      throw new Error('Cannot mint a secure access token: WebCrypto unavailable.');
    }
    const p = String(prefix || '').replace(/[^a-z0-9]/gi, '').slice(0, 12);
    return p ? `${p}_${rand}` : rand;
  };


  // ==========================================================================
  // TASK-1460 — canonical PERSON-NAME capitalizer (Kolsen 2026-07-31).
  // ==========================================================================
  // "force the first letter of a person's name to capitalise." Applied on
  // WRITE (add-person form, application intake) — never on render, or every
  // surface has to remember to call it.
  //
  // It ONLY ever upper-cases a letter. It never lower-cases anything, so
  // "McDonald", "O'Brien", "DeShawn", "LaToya" and every other intentional
  // inner capital survive untouched — the failure mode of a naive
  // charAt(0).toUpperCase() + slice(1).toLowerCase().
  //
  // Capitalizes the first letter of each whitespace token and of each
  // hyphen-separated part ("mary-jane" -> "Mary-Jane"), because a hyphenated
  // surname is two names. Apostrophes are deliberately NOT split on: "o'brien"
  // -> "O'brien" is a little short of ideal, but splitting there would turn
  // "Ma'ayan" into "Ma'Ayan", and inventing a capital inside someone's name is
  // worse than leaving one lowercase.
  //
  // Lowercase nobiliary particles are left exactly as typed — "de la Cruz",
  // "van der Berg", "bin Rashid". Someone who typed them lowercase meant it.
  // A particle in FIRST position is still capitalized (a name that begins
  // "De ..." is how it is written when it leads).
  const _NAME_PARTICLES = new Set([
    'de', 'del', 'dela', 'della', 'di', 'da', 'do', 'dos', 'du', 'des',
    'la', 'le', 'las', 'los', 'van', 'von', 'der', 'den', 'ter', 'ten',
    'bin', 'ibn', 'al', 'el', 'y', 'e', 'of'
  ]);

  window.capitalizePersonName = function capitalizePersonName(raw) {
    if (raw == null) return raw;
    const str = String(raw);
    if (!str.trim()) return str;
    let tokenIndex = -1;
    return str.replace(/\S+/g, function (token) {
      tokenIndex++;
      if (tokenIndex > 0 && _NAME_PARTICLES.has(token.toLowerCase())) return token;
      return token.replace(/(^|-)([a-z])/g, function (m, sep, ch) {
        return sep + ch.toUpperCase();
      });
    });
  };

  // Format namespace. Only byte-identical merges are routed here.
  window.Fmt = window.Fmt || {};

  // "$#,###" (no cents). Byte-identical merge of bi.js + billing.js formatCurrency.
  window.Fmt.currency = function (val) {
    return '$' + (val || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0, maximumFractionDigits: 0
    });
  };

  // "(xxx) xxx-xxxx". Byte-identical merge of app.js + hr.js formatPhoneDisplay
  // (10-digit and 11-digit-leading-1; otherwise returns the original input).
  window.Fmt.phone = function (phone) {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '1') {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  // ==========================================================================
  // TASK-9189 — OVERNIGHT-SAFE SHIFT TIME RANGE
  // ==========================================================================
  // A clock entry is two timestamps, and every surface printed them with
  // toLocaleTimeString, which is TIME-ONLY. A shift that crosses midnight then
  // renders a range that reads BACKWARDS. Chris's own entry
  // JXCgl4dC6PufCLfU0ZOd (in Aug 28 9:18 AM, out Aug 29 10:39 AM, totalHours
  // 25.35) printed as "9:18 AM – 10:39 AM" beside "25.35 hrs": 1h21m of range
  // next to a 25-hour number. MEASURED on TITS: 49 of 2174 completed entries
  // clock out on a LATER local calendar day, and the surface that renders them
  // is the WORKFORCE panel an admin approves hours on BEFORE a pay run — so the
  // ambiguity is a money defect, not a cosmetic one.
  //
  // The day marker is derived from the LOCAL CALENDAR DAY, never from elapsed
  // time. Those are different questions: a 3.23h shift (10:45 PM -> 1:58 AM,
  // entry 22QAXtZshvFGZYZhZr16) crosses midnight, while a 20h shift can sit
  // inside a single day-boundary crossing — so (out - in) / 86400000 answers
  // neither. Date.UTC() over the LOCAL y/m/d yields a calendar-day serial that
  // is immune to DST; a raw millisecond difference silently skews by an hour on
  // the two US transition days, which is exactly where an overnight shift is
  // most likely to land and least likely to be double-checked.

  // Firestore Timestamp | Date | parseable value -> valid Date, else null.
  // Never render a Firestore timestamp raw — this is the coercion every clock
  // surface shares.
  function _clockDate(v) {
    if (v == null) return null;
    const d = (v && typeof v.toDate === 'function')
      ? v.toDate()
      : (v instanceof Date ? v : new Date(v));
    return (d instanceof Date && !isNaN(d.getTime())) ? d : null;
  }
  window.Fmt.toDate = _clockDate;

  // Calendar-day serial in the VIEWER's local zone.
  //
  // 🔴 THE RULE ITSELF IS NOT WRITTEN HERE ANY MORE (TASK-9206 follow-up). It
  // lives ONCE, canonically, in functions/src/shared/clockDayOffset.js, and
  // scripts/build-shared.cjs — already wired into the hosting:app predeploy —
  // emits public/src/shared/clock-day-offset.js from those exact bytes. app.js
  // imports that mirror immediately before this file, so `window.ClockDay` is
  // defined by the time anything below runs. It is read at CALL time as a
  // window global, never a cross-`?v=` ES import (Pitfall #1).
  //
  // WHY: this same rule, hand-written in three places, caused three separate
  // bugs in one day — TASK-9189 (nine clock surfaces), TASK-9197 (the invoice /
  // ticket-line cells), TASK-9206 (the emailed field ticket, its PDF and the
  // approval portal). Generation makes drift impossible; a test only made it
  // detectable.
  //
  // THE ZONE IS AN EXPLICIT ARGUMENT, and the two runtimes pass different ones
  // on purpose: the browser asks for the VIEWER's day (RUNTIME_LOCAL_ZONE —
  // getFullYear/getMonth/getDate routed through Date.UTC, byte-identical to
  // what this function did before), while the server document asks for the
  // TENANT's IANA zone. The live overnight line 03:45Z -> 07:19Z is ONE
  // calendar day in UTC and TWO in America/Chicago, so collapsing the two would
  // be a bug, not a simplification.
  //
  // A missing window.ClockDay throws here on purpose: a silently wrong date on
  // a payroll panel is worse than a loud failure at the call site.
  function _localDaySerial(d) {
    return window.ClockDay.localDaySerial(d, window.ClockDay.RUNTIME_LOCAL_ZONE);
  }

  const _HHMM = { hour: 'numeric', minute: '2-digit' };

  // Canonical clock time-of-day. "9:18 AM".
  window.Fmt.clockTime = function (v) {
    const d = _clockDate(v);
    return d ? d.toLocaleTimeString('en-US', _HHMM) : '';
  };

  // The one shift-range formatter. Callers pick the field they need rather than
  // re-deriving the day math:
  //   inText      "9:18 AM"
  //   outText     "10:39 AM (+1d)"              plain text / CSV
  //   outHtml     "10:39 AM" + chip             HTML cells that already split in/out
  //   rangeText   "9:18 AM – 10:39 AM (+1d)"
  //   rangeHtml   same, with the chip
  //   spanHours   TRUE elapsed hours implied by the two timestamps
  //   dayOffset   local calendar days crossed (0 = same day)
  window.Fmt.shiftSpan = function (clockInVal, clockOutVal) {
    const cin = _clockDate(clockInVal);
    const cout = _clockDate(clockOutVal);
    const inText = cin ? cin.toLocaleTimeString('en-US', _HHMM) : '';

    if (!cin || !cout) {
      return {
        ok: false, cin: cin, cout: null, inText: inText,
        outText: '', outHtml: '', marker: '', markerHtml: '', outDateText: '',
        dayOffset: 0, crossesMidnight: false, spanHours: null,
        rangeText: inText, rangeHtml: inText
      };
    }

    const outTime = cout.toLocaleTimeString('en-US', _HHMM);
    const dayOffset = _localDaySerial(cout) - _localDaySerial(cin);
    const spanHours = (cout - cin) / 3600000;
    const outDateText = cout.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });

    // Airline-style compact marker: it has to survive a 400px phone column
    // without wrapping, which a full "Aug 29" date does not. The exact date
    // still reaches the reader through the tooltip.
    const marker = dayOffset > 0 ? ('+' + dayOffset + 'd') : '';
    const markerTitle = dayOffset > 0
      ? ('Clocked out ' + outDateText + (dayOffset === 1
          ? ' — the day after clocking in.'
          : ' — ' + dayOffset + ' days after clocking in.'))
      : '';

    // Amber-on-transparent so it reads on both the light panel and a dark theme.
    // var(--text-muted) on a light background is the contrast trap this codebase
    // has hit before, so the token carries a literal fallback.
    const markerHtml = marker
      ? '<span title="' + window.escAttr(markerTitle) + '"'
        + ' style="display:inline-block;margin-left:4px;padding:0 4px;'
        + 'border:1px solid var(--action-warning,#B45309);border-radius:8px;'
        + 'font-size:0.7em;font-weight:700;line-height:1.6;vertical-align:baseline;'
        + 'color:var(--action-warning,#B45309);white-space:nowrap;">' + marker + '</span>'
      : '';

    const outText = marker ? (outTime + ' (' + marker + ')') : outTime;
    const outHtml = outTime + markerHtml;

    return {
      ok: true, cin: cin, cout: cout, inText: inText,
      outText: outText, outHtml: outHtml,
      marker: marker, markerHtml: markerHtml, outDateText: outDateText,
      dayOffset: dayOffset, crossesMidnight: dayOffset > 0, spanHours: spanHours,
      rangeText: inText + ' – ' + outText,
      rangeHtml: inText + ' – ' + outHtml
    };
  };

  // ==========================================================================
  // TASK-9197 — OVERNIGHT-SAFE TICKET-LINE TIME **CELLS**
  // ==========================================================================
  // The ticket/invoice/print family is the half TASK-9189 deliberately deferred.
  // It is NOT the same rendering problem, and `shiftSpan` cannot serve it as-is:
  //
  //   shiftSpan builds ONE string in ONE place — "9:18 AM – 10:39 AM (+1d)" —
  //   where the "+1d" sits immediately beside the time it is relative to.
  //   A ticket line is TWO INDEPENDENT TABLE CELLS ("From" | "To") on a document
  //   whose header already names a date, and that is what breaks the chip: a
  //   ticket's `workDate` is the day the shift ENDED, so an overnight line very
  //   often STARTS the day before. Live TITS ticket 100671 (workDate 2026-09-10)
  //   carries a line running Sep 9 10:45 PM -> Sep 10 2:19 AM. Printing
  //   "10:45 PM | 2:19 AM (+1d)" under a "September 10" header reads as
  //   Sep 10 -> Sep 11. That is not an ambiguous answer, it is a WRONG one, and
  //   it would be printed on a customer invoice.
  //
  // So a CELL carries its own date, on BOTH ends, and only when the window
  // actually crosses a calendar day. Dating only the far end would leave the
  // near end anchored to nothing — which is the defect above.
  //
  // EXTENDED HERE, NOT FORKED: this shares `_clockDate` and `_localDaySerial`
  // with shiftSpan, so there is exactly ONE day-offset implementation in the
  // product and the clock surfaces and the money documents can never drift.
  // The local calendar day (Date.UTC over the LOCAL y/m/d) is the only thing
  // asked — never elapsed milliseconds. See the shiftSpan note above for why
  // floor(), round() and a raw ms difference are each wrong in a different
  // direction.
  //
  // MEASURED on TITS 2026-09-14: 59 of 2,456 ticketLines carrying BOTH times
  // cross a local calendar day, spread over 33 tickets, 17 of them already
  // marked invoiced.
  const _MONTHDAY = { month: 'short', day: 'numeric' };
  const _FULLDATE = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };

  // The date suffix is deliberately quiet — it is an annotation on a money
  // document, not a warning. `--text-secondary` carries a literal fallback
  // because the printed ticket and the PDF are standalone documents with no
  // theme tokens defined.
  //
  // The leading NBSP lives INSIDE the nowrap span on purpose: it separates the
  // date from the time for anything that strips the markup (a copy-paste out of
  // the invoice, a plain-text export, a screen reader) while leaving no break
  // opportunity that could orphan the date onto its own line in a narrow column.
  function _dateSuffixHtml(shortDate, fullDate) {
    return '<span title="' + window.escAttr(fullDate) + '"'
      + ' style="font-size:0.85em;white-space:nowrap;'
      + 'color:var(--text-secondary,#475569);">&nbsp;' + shortDate + '</span>';
  }

  // One from/to pair -> the two cells that render it.
  //   fromText/toText   plain text  (CSV, email text, anywhere HTML is wrong)
  //   fromHtml/toHtml   HTML cell   (ticket review, invoice, printed ticket)
  // When the window does NOT cross a day, every field is the bare time and the
  // output is byte-identical to what the old time-only formatters produced.
  window.Fmt.shiftCells = function (fromVal, toVal) {
    const f = _clockDate(fromVal);
    const t = _clockDate(toVal);
    const fTime = f ? f.toLocaleTimeString('en-US', _HHMM) : '';
    const tTime = t ? t.toLocaleTimeString('en-US', _HHMM) : '';

    // Offset is only meaningful with BOTH ends. A negative offset (a hand-edited
    // line whose "to" precedes its "from") dates both ends too — showing it is
    // how a biller sees the mistake instead of reading past it.
    const dayOffset = (f && t) ? (_localDaySerial(t) - _localDaySerial(f)) : 0;
    const crosses = dayOffset !== 0;

    function cell(d, time) {
      if (!crosses || !time || !d) return { text: time, html: time };
      const shortDate = d.toLocaleDateString('en-US', _MONTHDAY);
      const fullDate = d.toLocaleDateString('en-US', _FULLDATE);
      return {
        text: time + ' ' + shortDate,
        html: time + _dateSuffixHtml(shortDate, fullDate)
      };
    }

    const fc = cell(f, fTime);
    const tc = cell(t, tTime);
    return {
      ok: !!(f && t),
      dayOffset: dayOffset,
      crossesMidnight: crosses,
      fromText: fc.text, fromHtml: fc.html,
      toText: tc.text, toHtml: tc.html
    };
  };

  // TASK-9189 requirement 3 — the displayed TOTAL and the displayed RANGE have
  // to agree, or the range has merely been made to LOOK right while the number a
  // human approves still pays wrong.
  //
  // totalHours is ALWAYS round((clockOut - clockIn) / 3600000, 2): the client
  // (clock.js) and the server (ticketGateway.computeTotalHours) both derive it
  // that way, and the product has NO break/lunch/unpaid deduction anywhere. So a
  // stored total that disagrees with its own two timestamps is a DATA defect,
  // never a business rule — and until now the panel silently printed the STORED
  // number beside a range implying a different one, which is precisely how a
  // wrong total reaches a pay run unnoticed.
  //
  // MEASURED on TITS: 33 of 2174 completed entries disagree by more than the
  // rounding quantum — six of one driver's at ~0.99h OVER (a whole-hour smell,
  // i.e. real overpayment) and a run of another's at ~0.05h. This FLAGS and
  // never corrects: the stored numbers are displayed exactly as stored.
  //
  // Tolerance defaults to 0.02h — 4x the 0.005h worst case of rounding to 0.01 —
  // so honest rounding can never raise it.
  window.Fmt.shiftHoursDrift = function (spanHours, storedHours, tolerance) {
    if (typeof spanHours !== 'number' || !isFinite(spanHours)) return null;
    if (typeof storedHours !== 'number' || !isFinite(storedHours)) return null;
    const tol = (typeof tolerance === 'number' && isFinite(tolerance)) ? tolerance : 0.02;
    const delta = storedHours - spanHours;
    if (Math.abs(delta) <= tol) return null;
    return {
      delta: delta,
      spanHours: spanHours,
      storedHours: storedHours,
      text: 'Paid hours (' + storedHours.toFixed(2) + ') do not match the clock times ('
        + spanHours.toFixed(2) + ' hrs). Nothing has been auto-corrected — verify before approving.',
      short: (delta > 0 ? '+' : '') + delta.toFixed(2) + 'h vs clock times'
    };
  };
})();
